<template>
  <div>
    <div class="home-page animated fadeIn">
    <b-container class="py-5">
      <!-- Empty State -->
      <template v-if="isHomepageEmpty">
        <div class="empty-state text-center py-5">
          <div class="empty-state-icon mb-4">
            <i class="bi bi-house-door" style="font-size: 4rem; opacity: 0.3;"></i>
          </div>
          <h3 class="mb-3">{{ $t('homepageWelcome') }}</h3>
          <p class="text-muted">{{ $t('homepageEmptyDescription') }}</p>
          <b-button variant="primary" class="mt-3" to="/admin/marketplace">
            <b-icon icon="shop" class="mr-2"></b-icon>
            {{ $t('visitMarketplace') }}
          </b-button>
        </div>
      </template>

      <!-- Content -->
      <template v-else>
        <!-- Bookmarks Section -->
        <section v-if="bookmarks.length && $can('dashboard access')" class="home-section mb-5">
          <div class="section-header mb-4">
            <h5 class="section-title mb-0">
              <i class="bi bi-bookmark-star-fill me-2"></i>
              {{ $t('bookmarks') }}
            </h5>
          </div>

          <div class="items-grid">
            <div 
              v-for="bookmark in bookmarks" 
              :key="bookmark.uuid" 
              class="grid-item"
              @click="openComponent(bookmark)"
            >
              <div class="item-icon-wrapper">
                <i :class="'bi bi-' + bookmark.icon" v-if="bookmark.icon"></i>
                <span class="icon-letter" v-else>{{ getInitial(bookmark) }}</span>
              </div>
              <div class="item-name">{{ $translateLabel(bookmark.label, bookmark.name) }}</div>
            </div>
          </div>
        </section>

        <!-- Apps Section -->
        <section v-if="apps.length > 0 || integrations.length > 0" class="home-section mb-5">
          <div class="section-header mb-4">
            <h5 class="section-title mb-0">
              <i class="bi bi-grid-3x3-gap-fill me-2"></i>
              {{ $t('apps') }}
            </h5>
          </div>

          <div class="items-grid">
            <div 
              v-for="app in apps" 
              :key="app.uuid" 
              class="grid-item"
              @click="openApp(app)"
            >
              <div 
                v-if="app.type == 'forge-app' && $can('manage forge')" 
                :class="app.status === 'failed' ? 'status-badge' : app.update_available ? 'update-badge' : 'settings-badge'"
                @click.stop="app.status === 'failed' ? openReinstallConfirm(app) : app.update_available ? openUpdateDialog(app) : openAppSettings(app)"
                v-b-tooltip.hover
                :title="app.status === 'failed' ? ((app.status_message || $t('marketplace.installFailed')) + ' — ' + $t('marketplace.clickToReinstall')) : app.update_available ? $t('marketplace.updateAvailable') : $t('marketplace.settings')"
              >
                <b-icon v-if="app.status === 'failed'" icon="exclamation-triangle-fill"></b-icon>
                <b-icon v-else-if="app.update_available" icon="arrow-up-circle-fill"></b-icon>
                <b-icon v-else icon="gear-fill"></b-icon>
              </div>

              <div class="item-icon-wrapper">
                <template v-if="app.type == 'platform-app' || app.type == 'component'">
                  <i :class="'bi bi-' + app.icon" v-if="app.icon"></i>
                  <span class="icon-letter" v-else>{{ getInitial(app) }}</span>
                </template>
                <img :src="app.icon" v-else-if="app.type == 'forge-app'" class="icon-img"/>
                
                <span class="icon-letter" v-else>{{ getInitial(app) }}</span>
              </div>
              <div class="item-name">{{ getItemName(app) }}</div>
            </div>

            <RenderEvent 
              v-for="integration in integrations"
              :key="integration.id"
              class="grid-item"
              @click="openIntegration(integration)"
              tag="span" 
              event="home" 
              :only="integration.id"
            >
              <template  v-slot:default="{ elements }">
                <div v-for="element in elements" :key="element.context.id" class="item-icon-wrapper">
                  <component :is="element.default" :context="element.context">
                    <i :class="'bi bi-' + getIntegrationIconValue(element.context)" v-if="getIntegrationIconType(element.context) == 'b-icon'"></i>
                    <img :src="getIntegrationIconValue(element.context)" v-else-if="getIntegrationIconType(element.context) == 'img'" class="icon-img"/>
                    <span class="icon-letter" v-else>{{ getInitial(element.context) }}</span>
                  </component>
                </div>
                <div class="item-name">{{ getItemName(integration) }}</div>
              </template>

            </RenderEvent>
          </div>
        </section>

        <!-- Components Section -->
        <section v-if="components.length > 0" class="home-section mb-5">
          <div class="section-header mb-4">
            <h5 class="section-title mb-0">
              <i class="bi bi-box-seam-fill me-2"></i>
              {{ $t('components') }}
            </h5>
          </div>

          <div class="items-grid">
            <div 
              v-for="component in components" 
              :key="component.uuid" 
              class="grid-item"
              @click="openComponent(component)"
            >
              <div class="item-icon-wrapper">
                <i :class="'bi bi-' + component.icon" v-if="component.icon"></i>
                <span class="icon-letter" v-else>{{ getInitial(component) }}</span>
              </div>
              <div class="item-name">{{ $translateLabel(component.label, component.name) }}</div>
            </div>
          </div>
        </section>
      </template>
    </b-container>
    </div>
    
    <!-- App Settings Modal -->
    <main-dialog 
      v-model="showSettingsModal"
      :title="selectedApp ? selectedApp.name : ''"
      size="xl"
      hide-footer
      centered
    >
      <ForgeEdit v-if="selectedApp" :appUuid="selectedApp.uuid" />
    </main-dialog>

    <!-- Reinstall Confirmation Dialog -->
    <main-dialog
      :show="showReinstallModal"
      :title="$t('marketplace.reinstall')"
      :icon="reinstallApp ? reinstallApp.icon : null"
      @close="showReinstallModal = false"
    >
      <p>{{ $t('marketplace.confirmReinstall') }}</p>

      <div v-if="reinstallVersionInfo">
        <!-- Entitlements -->
        <div v-if="reinstallVersionInfo.entitlements && reinstallVersionInfo.entitlements.length" class="iw-section">
          <div class="iw-section-title iw-section-title--entitlement">
            <b-icon icon="star-fill" class="mr-1"></b-icon>
            {{ $t('marketplace.installWizard.entitlements') }}
          </div>
          <p class="text-muted small mb-2">{{ $t('marketplace.installWizard.entitlementsDescription') }}</p>
          <div v-for="(perm, idx) in reinstallVersionInfo.entitlements" :key="'ent-'+idx" class="iw-perm-row iw-perm-row--entitlement">
            <b-icon icon="star-fill" class="iw-perm-icon text-warning"></b-icon>
            <code class="iw-perm-name">{{ perm }}</code>
          </div>
        </div>

        <!-- Install Permissions -->
        <div v-if="reinstallVersionInfo.install_permissions && reinstallVersionInfo.install_permissions.length" class="iw-section">
          <div class="iw-section-title">
            <b-icon icon="shield-lock" class="mr-1"></b-icon>
            {{ $t('marketplace.installWizard.installPermissions') }}
          </div>
          <p class="text-muted small mb-3">{{ $t('marketplace.installWizard.installPermissionsDescription') }}</p>
          <div v-for="(perm, idx) in reinstallVersionInfo.install_permissions" :key="'rip-'+idx" class="iw-perm-row">
            <b-icon icon="shield-check" class="iw-perm-icon text-muted"></b-icon>
            <div>
              <code class="iw-perm-name">{{ perm.permission || perm }}</code>
              <small v-if="perm.reason" class="d-block text-muted mt-1">{{ perm.reason }}</small>
            </div>
          </div>
        </div>

        <!-- Runtime Permissions -->
        <div v-if="reinstallVersionInfo.runtime_permissions && reinstallVersionInfo.runtime_permissions.length" class="iw-section">
          <div class="iw-section-title">
            <b-icon icon="cpu" class="mr-1"></b-icon>
            {{ $t('marketplace.installWizard.runtimePermissions') }}
          </div>
          <p class="text-muted small mb-2">{{ $t('marketplace.installWizard.runtimePermissionsDescription') }}</p>
          <div v-for="(perm, idx) in reinstallVersionInfo.runtime_permissions" :key="'rrp-'+idx" class="iw-perm-row">
            <b-icon icon="lightning-fill" class="iw-perm-icon text-muted"></b-icon>
            <code class="iw-perm-name">{{ perm }}</code>
          </div>
        </div>

        <!-- No Permissions -->
        <div
          v-if="(!reinstallVersionInfo.install_permissions || !reinstallVersionInfo.install_permissions.length) && (!reinstallVersionInfo.runtime_permissions || !reinstallVersionInfo.runtime_permissions.length) && (!reinstallVersionInfo.entitlements || !reinstallVersionInfo.entitlements.length)"
          class="text-center py-2"
        >
          <b-icon icon="check-circle-fill" variant="success" font-scale="1.5"></b-icon>
          <p class="mt-2 mb-0 text-muted small">{{ $t('marketplace.updateDialog.noPermissionChanges') }}</p>
        </div>
      </div>
      <div v-else class="text-center py-4">
        <b-spinner small></b-spinner>
      </div>

      <template #footer>
        <b-button variant="outline-secondary" @click="showReinstallModal = false">{{ $t('cancel') }}</b-button>
        <b-button variant="primary" :disabled="!reinstallVersionInfo" @click="executeReinstall">{{ $t('approve') }}</b-button>
      </template>
    </main-dialog>

    <!-- Update Available Modal -->
    <main-dialog
      :show="showUpdateModal"
      :title="$t('marketplace.updateDialog.title')"
      :icon="updateApp ? updateApp.icon : null"
      @close="showUpdateModal = false"
    >
      <div v-if="updateVersionInfo">
        <div class="d-flex align-items-center justify-content-between mb-4 p-3 version-change-row">
          <div class="text-center">
            <div class="small text-muted mb-1">{{ $t('marketplace.updateDialog.installedVersion') }}</div>
            <strong>{{ updateVersionInfo.installed_version }}</strong>
          </div>
          <b-icon icon="arrow-right" font-scale="1.5" class="text-muted mx-3"></b-icon>
          <div class="text-center">
            <div class="small text-muted mb-1">{{ $t('marketplace.updateDialog.latestVersion') }}</div>
            <strong class="text-success">{{ updateVersionInfo.latest_version }}</strong>
          </div>
        </div>

        <!-- Install Permissions -->
        <div v-if="updateVersionInfo.install_permissions && updateVersionInfo.install_permissions.length" class="iw-section">
          <div class="iw-section-title">
            <b-icon icon="shield-lock" class="mr-1"></b-icon>
            {{ $t('marketplace.installWizard.installPermissions') }}
          </div>
          <p class="text-muted small mb-3">{{ $t('marketplace.installWizard.installPermissionsDescription') }}</p>
          <div
            v-for="(perm, idx) in updateVersionInfo.install_permissions"
            :key="idx"
            class="iw-perm-row"
          >
            <b-icon icon="shield-check" class="iw-perm-icon text-muted"></b-icon>
            <div>
              <code class="iw-perm-name">{{ perm.permission }}</code>
              <small v-if="perm.reason" class="d-block text-muted mt-1">{{ perm.reason }}</small>
            </div>
          </div>
        </div>

        <!-- Runtime Permissions (collapsible) -->
        <div v-if="updateVersionInfo.runtime_permissions && updateVersionInfo.runtime_permissions.length" class="iw-section">
          <button class="iw-expand-btn" @click="updateRuntimePermsExpanded = !updateRuntimePermsExpanded">
            <b-icon icon="cpu" class="mr-1"></b-icon>
            {{ $t('marketplace.installWizard.runtimePermissions') }}
            <span class="iw-expand-count">{{ updateVersionInfo.runtime_permissions.length }}</span>
            <b-icon :icon="updateRuntimePermsExpanded ? 'chevron-up' : 'chevron-down'" class="ml-auto"></b-icon>
          </button>
          <div v-if="updateRuntimePermsExpanded">
            <p class="text-muted small mb-2 mt-2">{{ $t('marketplace.installWizard.runtimePermissionsDescription') }}</p>
            <div v-for="(perm, idx) in updateVersionInfo.runtime_permissions" :key="idx" class="iw-perm-row">
              <b-icon icon="lightning-fill" class="iw-perm-icon text-muted"></b-icon>
              <code class="iw-perm-name">{{ perm }}</code>
            </div>
          </div>
        </div>

        <!-- Exposed Permissions (collapsible) -->
        <div v-if="updateVersionInfo.permissions && updateVersionInfo.permissions.length" class="iw-section">
          <button class="iw-expand-btn" @click="updateExposedPermsExpanded = !updateExposedPermsExpanded">
            <b-icon icon="people" class="mr-1"></b-icon>
            {{ $t('marketplace.installWizard.exposedPermissions') }}
            <span class="iw-expand-count">{{ updateVersionInfo.permissions.length }}</span>
            <b-icon :icon="updateExposedPermsExpanded ? 'chevron-up' : 'chevron-down'" class="ml-auto"></b-icon>
          </button>
          <div v-if="updateExposedPermsExpanded">
            <p class="text-muted small mb-2 mt-2">{{ $t('marketplace.installWizard.exposedPermissionsDescription') }}</p>
            <div v-for="(perm, idx) in updateVersionInfo.permissions" :key="idx" class="iw-perm-row">
              <b-icon icon="person-badge" class="iw-perm-icon text-muted"></b-icon>
              <div>
                <code class="iw-perm-name">{{ perm.permission }}</code>
                <small v-if="perm.label" class="d-block text-muted mt-1">{{ $translateLabel(perm.label) }}</small>
                <small v-if="perm.description" class="d-block text-muted">{{ $translateLabel(perm.description) }}</small>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!updateVersionInfo.install_permissions.length && !updateVersionInfo.runtime_permissions.length && !updateVersionInfo.permissions.length"
          class="text-center py-2"
        >
          <b-icon icon="check-circle-fill" variant="success" font-scale="1.5"></b-icon>
          <p class="mt-2 mb-0 text-muted small">{{ $t('marketplace.updateDialog.noPermissionChanges') }}</p>
        </div>
      </div>
      <div v-else class="text-center py-4">
        <b-spinner small></b-spinner>
      </div>

      <template #footer>
        <b-button variant="outline-secondary" @click="showUpdateModal = false">{{ $t('cancel') }}</b-button>
        <b-button variant="primary" :disabled="updating || !updateVersionInfo" @click="executeUpdate">
          <b-spinner small v-if="updating" class="mr-1"></b-spinner>
          {{ updating ? $t('marketplace.updating') : $t('marketplace.update') }}
        </b-button>
      </template>
    </main-dialog>
  </div>
</template>


<style scoped lang="scss">
.home-page {
  min-height: calc(100vh - 100px);
  background: var(--body-bg);
}

.empty-state {
  max-width: 600px;
  margin: 100px auto;
  padding: 60px 40px;
  background: var(--card-bg, var(--form-bg));
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.empty-state-icon {
  color: var(--primary);
}

.home-section {
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-header {
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
  
  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
    
    i {
      color: #6c80ff;
      font-size: 1.1rem;
    }
  }
  
  .badge {
    font-size: 0.75rem;
    padding: 0.35rem 0.65rem;
    background: linear-gradient(135deg, #6c80ff, #764ba2) !important;
  }
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
  
  @media (max-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: 1.5rem;
  }
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 2.5rem;
  }
}

.grid-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  padding: 0.5rem;
  border-radius: 12px;
  
  &:hover {
    transform: translateY(-8px);
    
    .item-icon-wrapper {
      background: linear-gradient(135deg, #6c80ff, #764ba2);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.35);
      
      i, .icon-letter {
        color: white;
      }
    }
    
    .item-name {
      color: #6c80ff;
    }
  }
}

.item-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: var(--form-bg);
  border: 2px solid var(--border-color, rgba(0, 0, 0, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  
  i {
    font-size: 2.5rem;
    color: #6c80ff;
    transition: color 0.3s ease;
  }
  
  .icon-letter {
    font-size: 2rem;
    font-weight: 700;
    color: #6c80ff;
    transition: color 0.3s ease;
  }
  
  .icon-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.item-name {
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  color: var(--text-primary);
  transition: color 0.3s ease;
  word-break: break-word;
  max-width: 100%;
  line-height: 1.3;
}

// Settings badge on forge apps
.settings-badge {
  position: absolute;
  top: 4px;
  right: 15px;
  width: 24px;
  height: 24px;
  background: rgba(102, 126, 234, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  
  svg {
    font-size: 12px;
    color: white;
  }
  
  &:hover {
    background: #6c80ff;
    transform: scale(1.1) rotate(90deg);
  }
}

.grid-item:hover .settings-badge {
  opacity: 1;
}

.status-badge {
  position: absolute;
  top: 4px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: #dc3545;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.5);
  animation: pulse 2s infinite;
  
  svg {
    font-size: 12px;
    color: white;
  }
}

.update-badge {
  position: absolute;
  top: 4px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: #fd7e14;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(253, 126, 20, 0.25);
  animation: pulse-orange 2s infinite;

  svg {
    font-size: 12px;
    color: white;
  }

  &:hover {
    background: #e8690a;
    transform: scale(1.1);
  }
}

@keyframes pulse-orange {
  0%, 100% {
    box-shadow: 0 1px 4px rgba(253, 126, 20, 0.2);
  }
  50% {
    box-shadow: 0 1px 6px rgba(253, 126, 20, 0.35);
  }
}

.version-change-row {
  background: var(--form-bg);
  border-radius: 8px;
}

.iw-section {
  margin-bottom: 1.25rem;
}
.iw-section:last-child { margin-bottom: 0; }

.iw-section-title {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  margin-bottom: 0.65rem;
}

.iw-perm-row {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.35rem;
  background: var(--form-bg);
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
}
.iw-perm-icon { font-size: 0.85rem; margin-top: 3px; flex-shrink: 0; }
.iw-perm-name { font-size: 0.82rem; color: var(--text-primary); }

.iw-section-title--entitlement { color: #b45309; }
.iw-perm-row--entitlement {
  background: rgba(245, 158, 11, 0.08);
  border-color: rgba(245, 158, 11, 0.3);
}

.iw-expand-btn {
  display: flex;
  align-items: center;
  width: 100%;
  background: var(--form-bg);
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  cursor: pointer;
  gap: 0.35rem;
  transition: border-color 0.15s, background 0.15s;
}
.iw-expand-btn:hover {
  color: var(--text-primary);
}
.iw-expand-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--border-color, rgba(0,0,0,0.1));
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--text-secondary);
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.5);
  }
  50% {
    box-shadow: 0 2px 16px rgba(220, 53, 69, 0.8);
  }
}
</style>



<script>
import _ from 'lodash'
import Base from '../components/widgets/Base'
import ForgeEdit from './developer/forge/Edit'
import RenderEvent from '../components/integrations/RenderEvent.vue';
import { openForgeAppWithToken } from '@/helpers/forgeTokenLaunch'

export default {
  name: 'home',
  components: {
    Base,
    ForgeEdit,
    RenderEvent,
  },
  data: function () {
    return {
      showSettingsModal: false,
      selectedApp: null,
      showReinstallModal: false,
      reinstallApp: null,
      reinstallVersionInfo: null,
      showUpdateModal: false,
      updateApp: null,
      updateVersionInfo: null,
      updating: false,
      updateRuntimePermsExpanded: false,
      updateExposedPermsExpanded: false,
      updatePoller: null,
      pendingUpdateApp: null,
    }
  },
  computed: {
    apps() {
      const apps = this.$store.getters.getApps.map(app => {
        app.type = "platform-app"

        return app
      })

      const forgeApps = this.$store.getters.getForgeApps.filter(app => app.app_type !== 'service').map(app => {
        app.type = "forge-app"

        return app
      })

      return apps.concat(forgeApps)
    },

    integrations() {
      return this.$store.getters.getActiveIntegrations
    },

    bookmarks: {
      get() {
        let bookmarks = _.get(this.$store.getters.projectUserConfig("bookmarks"), "data", {})

        return Object.values(bookmarks).map(c => {
          return this.$store.getters.componentsMenu.find(f => {
              return c.name == f.name
          })
        }).filter(b => {
          if (!b) return false
          
          if (!b.is_active) {
            return false
          }

          if (b.is_public && this.$store.getters.isLiveEnv) {
            return true
          }

          return !this.$store.getters.isLiveEnv
        })
      },

      set(val) {
        let bookmarks = {}
        Object.values(val).forEach(v => {
          bookmarks[v.name] = v
        })

        this.$store.dispatch('updateProjectUserConfig', {path: "bookmarks", data: bookmarks})
      }
    },

    components() {
      let componentsMenu = this.$store.getters.componentsMenu.filter(c => {
        return c.is_public
      })

      componentsMenu.sort((a, b) => {
        return a.sort - b.sort
      })

      return componentsMenu.map(c => {
        c.type = "component"
        return c
      })
    },

    isHomepageEmpty() {
      return (this.components.length + this.bookmarks.length + this.apps.length + this.integrations.length) < 1
    }
  },
  methods: {
    async openApp(app) {
      if (app.type == "platform-app") {   
        window.open(this.$router.resolve(`/app/${app.name}`).href, "_blank")
      }

      if (app.type == "forge-app") {
        let url = this.$env.get("FORGE_APP_URL_PATTERN").replaceAll("{name}", app.name)
        url = `${url}/launch?env=${this.$store.getters.getCurrentEnv}&lang=${this.$store.getters.getLocale}&name=${app.name}&uuid=${app.uuid}`

        if (url.startsWith("http://localhost")) {
          url = `${url}&name=${app.name}`
        }

        openForgeAppWithToken(this, url, app.name)
      }

      if (app.type == "integration") {
        this.openIntegration(app)
      }
    },

    openBookmark(bookmark) {
      this.$router.push(bookmark.url)
    },

    openIntegration(int) {
      this.$router.push(`/integrations/page/${int.id}`)
    },

    openComponent(component) {
      let ref = component.name
      if (component.scope) {
        ref = `${component.scope}/${ref}`
      }

      this.$router.push(`/home/component/${ref}`)
    },

    openPortal(portal) {
      const pattern = this.$env.get("PORTAL_URL_PATTERN")
      const link = pattern.replaceAll("{name}", portal.name)
      const project = this.$store.getters.getCurrentProject
      const env = this.$store.getters.getCurrentEnv

      window.open(`${link}/#/?project=${project.uuid}&env=${env}`, "_blank")
    },
    
    openAppSettings(app) {
      this.selectedApp = app
      this.showSettingsModal = true
    },

    async openReinstallConfirm(app) {
      this.reinstallApp = app
      this.reinstallVersionInfo = null
      this.showReinstallModal = true
      try {
        var resp = await this.$http.get('/luma/appservice/v1/forge/' + app.name + '/settings')
        var version = app.dev_version || app.public_version
        var settings = (resp.data && resp.data.settings) || []
        var vs = null
        for (var i = 0; i < settings.length; i++) {
          if (settings[i].version === version) { vs = settings[i]; break }
        }
        this.reinstallVersionInfo = vs || { install_permissions: [], runtime_permissions: [], entitlements: [] }
      } catch (e) {
        console.error('[Reinstall] Failed to fetch settings', e)
        this.reinstallVersionInfo = { install_permissions: [], runtime_permissions: [], entitlements: [] }
      }
    },

    executeReinstall() {
      this.showReinstallModal = false
      const app = this.reinstallApp
      if (!app) return

      const version = app.dev_version || app.public_version
      const popup = this.$loadingPopup(this.$t('marketplace.reinstallingApp'))
      this.$http.patch(`/luma/appservice/v1/forge/${app.name}/settings?reinstall=${version}`, {})
        .then(() => {
          this.$store.dispatch('refreshForgeApps')
          this.$bvToast.toast(this.$t('marketplace.appReinstalled'), {
            variant: 'success',
            title: this.$t('success'),
            autoHideDelay: 3000
          })
        })
        .catch(err => {
          const errorMessage = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : this.$t('marketplace.failedToReinstallApp')
          this.$bvToast.toast(errorMessage, {
            variant: 'danger',
            title: this.$t('error')
          })
        })
        .finally(() => popup.hide())
    },

    async openUpdateDialog(app) {
      this.updateApp = app
      this.updateVersionInfo = null
      this.showUpdateModal = true
      this.updateRuntimePermsExpanded = false
      this.updateExposedPermsExpanded = false
      try {
        const env = this.$store.getters.getCurrentEnv
        const resp = await this.$http.get(`/luma/appservice/v1/forge/${app.name}/check-version`, {
          headers: { 'X-Project-Env': env }
        })
        this.updateVersionInfo = resp.data
      } catch (e) {
        console.error('[Update] Failed to fetch version info', e)
        this.showUpdateModal = false
        this.$toasted.error(this.$t('marketplace.failedToUpdate'))
      }
    },

    async executeUpdate() {
      if (!this.updateApp || this.updating) return
      this.updating = true
      const appName = this.updateApp.name
      try {
        const env = this.$store.getters.getCurrentEnv
        await this.$http.post(
          `/luma/appservice/v1/forge/${appName}/apply-update`,
          {},
          { headers: { 'X-Project-Env': env } }
        )
        this.$toasted.success(this.$t('marketplace.appUpdateInitiated'))

        // Poll every 5s for up to 2 minutes to detect completion
        this.pendingUpdateApp = appName
        let pollCount = 0
        const maxPolls = 24

        if (this.updatePoller) clearInterval(this.updatePoller)
        this.updatePoller = setInterval(async () => {
          pollCount++
          await this.$store.dispatch('refreshForgeApps')
          const apps = this.$store.getters.getForgeApps
          const app = apps.find(a => a.name === appName)
          const finished = !app || !app.update_available || app.status === 'failed' || pollCount >= maxPolls

          if (finished) {
            clearInterval(this.updatePoller)
            this.updatePoller = null
            this.pendingUpdateApp = null

            if (app && app.status === 'failed') {
              this.$bvToast.toast(app.status_message || this.$t('marketplace.failedToUpdate'), {
                variant: 'danger',
                title: this.$t('error'),
                noAutoHide: true
              })
            } else if (app && !app.update_available) {
              this.$bvToast.toast(this.$t('marketplace.appUpdateCompleted'), {
                variant: 'success',
                title: this.$t('success'),
                autoHideDelay: 5000
              })
            }
          }
        }, 5000)
      } catch (e) {
        console.error('[Update] Failed to apply update', e)
        this.$toasted.error(this.$t('marketplace.failedToUpdate'))
      } finally {
        this.updating = false
        this.showUpdateModal = false
        this.updateApp = null
        this.updateVersionInfo = null
      }
    },

    getInitial(item) {
      const name = this.$translateLabel(item.label, item.name)
      if (!name || name.length === 0) {
        return item.name ? item.name[0].toUpperCase() : '?'
      }
      return name[0].toUpperCase()
    },

    getIntegrationIconType(integration) {
      if (!integration.template.icon ) return null
      const part = integration.template.icon.split(':')
      return part[0]
    },

    getIntegrationIconValue(integration) {
      if (!integration.template.icon) return ''
      const type = this.getIntegrationIconType(integration)
      return integration.template.icon.replace(`${type}:`, '')
    },

    getItemName(item) {
      if (item.type === 'integration') {
        // For integrations, use template.label or label for translation
        const label = (item.template && item.template.label) || item.label || item.name
        return this.$translateLabel(label, item.id)
      }
      
      // For other types, prefer label translation over raw name
      if (item.label) {
        return this.$translateLabel(item.label, item.name)
      }
      return item.name
    }
  }
}
</script>
